import { Radio, RadioGroup } from "react-aria-components";

import styles from "./scope-chips.module.css";

export interface ScopeChip<Key extends string> {
  key: Key;
  label: string;
  /**
   * How many rows the chip answers for, where that is knowable.
   *
   * Optional because it is only knowable when the filter is applied over a set
   * already in hand. A chip that narrows on the wire has read one of its own
   * options and nothing about the others, and a count guessed from the page it
   * did read is a number that changes meaning with the filter - so those chips
   * carry no count rather than a wrong one.
   */
  count?: number;
}

/**
 * The status filter above the table.
 *
 * Chips rather than a dropdown so the options are all visible at once, and -
 * where a count is available - the row answers "how much is waiting on me"
 * before anything is clicked, which is the first question a floor manager has.
 *
 * `overrideNote`, when present, replaces the row's authority: a search widens the
 * view to every status, and saying so is what stops the chips reading as a lie.
 */
export function ScopeChips<Key extends string>({
  chips,
  selected,
  onSelect,
  ariaLabel,
  overrideNote,
}: {
  chips: ReadonlyArray<ScopeChip<Key>>;
  selected: Key;
  onSelect: (key: Key) => void;
  ariaLabel: string;
  overrideNote?: string;
}) {
  return (
    <RadioGroup
      className={styles.chips}
      aria-label={ariaLabel}
      value={selected}
      // A RadioGroup can be cleared to null by react-aria; the scope cannot be.
      onChange={(value) => onSelect(value as Key)}
      data-test-role="scope-chips"
    >
      {chips.map((chip) => (
        <Radio
          key={chip.key}
          value={chip.key}
          className={styles.chip}
          data-test-role="scope-chip"
          data-scope={chip.key}
        >
          {chip.label}
          {typeof chip.count === "number" ? (
            <span className={styles.count}>{chip.count}</span>
          ) : (
            <></>
          )}
        </Radio>
      ))}
      {overrideNote ? (
        <span className={styles.override} data-test-role="scope-override">
          {overrideNote}
        </span>
      ) : (
        <></>
      )}
    </RadioGroup>
  );
}
